import { apiClient } from "@/api/client";
import type { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { parseApiResponse, savingInstitutionSchema, savingPocketSchema } from "@/api/responseSchemas";
import { z } from "zod";
import { captureEntityMutation } from "@/analytics/posthog";

export interface SavingInstitution {
  id: number;
  user_id: number;
  name: string;
  active: boolean;
  description?: string | null;
}

export interface SavingPocket {
  id: number;
  user_id: number;
  saving_institution_id: number;
  name: string;
  amount: number;
  anual_rate: number;
  monthly_return?: number | null;
  yearly_return?: number | null;
  active: boolean;
  freeze_time?: number | null;
  freeze_date?: string | null;
  description?: string | null;
  unfreeze_date?: string | null;
  return_amount_after_freeze?: number | null;
}

export interface SavingInstitutionInput {
  name: string;
  description?: string;
  active?: boolean;
}

export interface SavingPocketInput {
  saving_institution_id: number;
  name: string;
  amount: number;
  anual_rate: number;
  freeze_time?: number | null;
  freeze_date?: string | null;
  description?: string;
  active?: boolean;
}

const isMethodNotAllowed = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  ((error as AxiosError).response?.status === 405 || (error as AxiosError).response?.status === 307 || (error as AxiosError).response?.status === 308);

const postWithPathFallback = async <T,>(path: string, data: unknown, schema: z.ZodType<T>) => {
  try {
    const res = await apiClient.post(path, data);
    return parseApiResponse(schema, res.data, `POST ${path}`);
  } catch (error) {
    if (!isMethodNotAllowed(error)) throw error;
    const fallbackPath = path.endsWith("/") ? path.slice(0, -1) : `${path}/`;
    const res = await apiClient.post(fallbackPath, data);
    return parseApiResponse(schema, res.data, `POST ${fallbackPath}`);
  }
};

const patchWithPathFallback = async <T,>(path: string, data: unknown, schema: z.ZodType<T>) => {
  try {
    const res = await apiClient.patch(path, data);
    return parseApiResponse(schema, res.data, `PATCH ${path}`);
  } catch (error) {
    if (!isMethodNotAllowed(error)) throw error;
    const fallbackPath = path.endsWith("/") ? path.slice(0, -1) : `${path}/`;
    const res = await apiClient.patch(fallbackPath, data);
    return parseApiResponse(schema, res.data, `PATCH ${fallbackPath}`);
  }
};

const deleteWithPathFallback = async (path: string): Promise<unknown> => {
  try {
    const res = await apiClient.delete(path);
    return res.data;
  } catch (error) {
    if (!isMethodNotAllowed(error)) throw error;
    const fallbackPath = path.endsWith("/") ? path.slice(0, -1) : `${path}/`;
    const res = await apiClient.delete(fallbackPath);
    return res.data;
  }
};

type SavingPocketQueryOptions = {
  enabled?: boolean;
};

export const useSavingInstitutions = (options?: SavingPocketQueryOptions) =>
  useQuery({
    queryKey: ["saving-institutions"],
    queryFn: async () => {
      const res = await apiClient.get("/savings/institutions");
      return parseApiResponse(savingInstitutionSchema.array(), res.data, "GET /savings/institutions");
    },
    enabled: FEATURE_FLAGS.savingPockets && (options?.enabled ?? true),
  });

export const useSavingPockets = (options?: SavingPocketQueryOptions) =>
  useQuery({
    queryKey: ["saving-pockets"],
    queryFn: async () => {
      const res = await apiClient.get("/savings/pockets");
      return parseApiResponse(savingPocketSchema.array(), res.data, "GET /savings/pockets");
    },
    enabled: FEATURE_FLAGS.savingPockets && (options?.enabled ?? true),
  });

const invalidateSavingPocketQueries = async (queryClient: ReturnType<typeof useQueryClient>) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["saving-institutions"] }),
    queryClient.invalidateQueries({ queryKey: ["saving-pockets"] }),
  ]);
};

export const useCreateSavingInstitution = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SavingInstitutionInput) => {
      return postWithPathFallback("/savings/institutions", data, savingInstitutionSchema);
    },
    onError: (error) => {
      captureEntityMutation({ entity: "saving_pocket", operation: "create", result: "failure", error });
    },
    onSuccess: async () => {
      captureEntityMutation({ entity: "saving_pocket", operation: "create", result: "success" });
      await invalidateSavingPocketQueries(queryClient);
    },
  });
};

export const useUpdateSavingInstitution = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<SavingInstitutionInput> }) => {
      return patchWithPathFallback(`/savings/institutions/${id}`, data, z.boolean());
    },
    onError: (error) => {
      captureEntityMutation({ entity: "saving_pocket", operation: "update", result: "failure", error });
    },
    onSuccess: async () => {
      captureEntityMutation({ entity: "saving_pocket", operation: "update", result: "success" });
      await invalidateSavingPocketQueries(queryClient);
    },
  });
};

export const useDeleteSavingInstitution = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return deleteWithPathFallback(`/savings/institutions/${id}`);
    },
    onSuccess: async () => {
      await invalidateSavingPocketQueries(queryClient);
    },
  });
};

export const useCreateSavingPocket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SavingPocketInput) => {
      return postWithPathFallback("/savings/pockets", data, savingPocketSchema);
    },
    onError: (error) => {
      captureEntityMutation({ entity: "saving_pocket", operation: "create", result: "failure", error });
    },
    onSuccess: async () => {
      captureEntityMutation({ entity: "saving_pocket", operation: "create", result: "success" });
      await invalidateSavingPocketQueries(queryClient);
    },
  });
};

export const useUpdateSavingPocket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<SavingPocketInput> }) => {
      return patchWithPathFallback(`/savings/pockets/${id}`, data, z.boolean());
    },
    onError: (error) => {
      captureEntityMutation({ entity: "saving_pocket", operation: "update", result: "failure", error });
    },
    onSuccess: async () => {
      captureEntityMutation({ entity: "saving_pocket", operation: "update", result: "success" });
      await invalidateSavingPocketQueries(queryClient);
    },
  });
};

export const useDeleteSavingPocket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return deleteWithPathFallback(`/savings/pockets/${id}`);
    },
    onSuccess: async () => {
      await invalidateSavingPocketQueries(queryClient);
    },
  });
};
